import { useState } from "react";
import { Link } from "react-router-dom";
import EmiteLogo from "@/components/base/EmiteLogo";
import StepIndicator from "./components/StepIndicator";
import Step1Titular from "./components/Step1Titular";
import Step2Empresa from "./components/Step2Empresa";
import Step3Config from "./components/Step3Config";
import Step4Confirm from "./components/Step4Confirm";
import SuccessScreen from "./components/SuccessScreen";
import SupportButton from "@/components/base/SupportButton";
import { API_URL } from "@/lib/api";
import { saasSignInUrl } from "@/lib/app";
import { publicDocsUrl } from "@/lib/docs";

const STEP_LABELS = ["Titular", "Empresa", "Fiscal", "Confirmación"];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SignupPage() {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [bindToken, setBindToken] = useState("");
  const [companyPublicId, setCompanyPublicId] = useState("");

  const [titular, setTitular] = useState({
    nombre: "",
    correo: "",
    contrasena: "",
    telefono: "",
    terminos: false,
  });

  const [empresa, setEmpresa] = useState({
    rnc: "",
    razonSocial: "",
    nombreComercial: "",
    direccion: "",
    provincia: "",
    municipio: "",
    actividad: "",
  });

  const [config, setConfig] = useState({
    ambiente: "testecf",
    erp: "",
    volumen: "",
    tieneP12: false,
    p12File: null as File | null,
    p12Password: "",
  });

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      let certificateP12: string | null = null;
      if (config.tieneP12 && config.p12File) {
        certificateP12 = await fileToBase64(config.p12File);
      }

      const body = {
        rnc: empresa.rnc,
        name: empresa.razonSocial,
        trade_name: empresa.nombreComercial || null,
        address: empresa.direccion || null,
        email: titular.correo,
        phone: titular.telefono || null,
        contact_name: titular.nombre || null,
        environment: config.ambiente === "testecf" ? "TesteCF" : config.ambiente === "certecf" ? "CerteCF" : "eCF",
        certificate_p12: certificateP12,
        certificate_pin: config.tieneP12 ? config.p12Password : null,
        erp: config.erp || null,
        volume: config.volumen || null,
      };

      const res = await fetch(`${API_URL}/public/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Error ${res.status}: No se pudo completar el registro.`);
      }

      const data = await res.json();
      setApiKey(data.api_key || "");
      setBindToken(typeof data.bind_token === "string" ? data.bind_token : "");
      setCompanyPublicId(
        typeof data.company_public_id === "string" ? data.company_public_id : "",
      );
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Error inesperado al registrar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <EmiteLogo size={32} className="text-blue-800" />
          <span className="font-bold text-blue-800">emite.do</span>
        </Link>
        <a href={publicDocsUrl()} className="text-sm text-slate-500 hover:text-slate-800 cursor-pointer">
          ¿Necesitas ayuda?
        </a>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-xl">
          {!done ? (
            <>
              {/* Step indicator */}
              <div className="mb-8">
                <StepIndicator currentStep={step} totalSteps={4} labels={STEP_LABELS} />
              </div>

              {/* Step card */}
              <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                {step === 1 && (
                  <Step1Titular data={titular} onChange={setTitular} onNext={() => setStep(2)} />
                )}
                {step === 2 && (
                  <Step2Empresa data={empresa} onChange={setEmpresa} onNext={() => setStep(3)} onBack={() => setStep(1)} />
                )}
                {step === 3 && (
                  <Step3Config data={config} onChange={setConfig} onNext={() => setStep(4)} onBack={() => setStep(2)} />
                )}
                {step === 4 && (
                  <Step4Confirm
                    data={{ titular, empresa, config }}
                    onBack={() => setStep(3)}
                    onSubmit={handleSubmit}
                    loading={loading}
                    error={error}
                  />
                )}
              </div>

              <p className="text-center text-sm text-slate-400 mt-6">
                ¿Ya tienes cuenta?{" "}
                <a href={saasSignInUrl()} className="text-blue-800 hover:underline cursor-pointer">
                  Iniciar sesión
                </a>
              </p>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
              <SuccessScreen
                tieneP12={config.tieneP12 && !!config.p12File}
                apiKey={apiKey}
                bindToken={bindToken}
                companyPublicId={companyPublicId}
              />
            </div>
          )}
        </div>
      </main>

      <SupportButton />
    </div>
  );
}
